/**
 * ADServiceGpoSection — GPOs documentadas de un servicio AD.
 *
 * Las GPOs se guardan en `Service.config_json.ad_topology.gpos`. Antes eran
 * tres campos de texto libre (nombre / aplicada a / descripción); ahora se
 * estructuran para poder documentar de verdad el diseño de directivas de
 * grupo: a qué se vinculan (dominio, sitio u OU), en qué orden, si están
 * forzadas (enforced) y si están habilitadas o deshabilitadas.
 *
 * Se conservan las claves antiguas (`target`, `description`) para no perder
 * datos ya guardados.
 *
 * Cuando la GPO se vincula a una OU ("Vinculada a: Unidad organizativa") se
 * puede elegir del catálogo de OUs del servicio (`target_ou_id`): el destino se
 * rellena con su DN y, si la OU se renombra o se mueve, el backend lo actualiza.
 * Escribir el destino a mano suelta ese vínculo.
 */
import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { buildOuDns } from './ADServiceOuSection';

const TARGET_TYPES = [
  ['domain', 'Dominio'],
  ['site', 'Sitio'],
  ['ou', 'Unidad organizativa (OU)'],
];

const STATUSES = [
  ['enabled', 'Habilitada'],
  ['disabled', 'Deshabilitada'],
  ['all_disabled', 'Deshabilitada (ambas partes)'],
];

const TARGET_PLACEHOLDER = {
  domain: 'ej. corp.empresa.local',
  site: 'ej. Madrid',
  ou: 'ej. OU=Servidores,OU=Madrid,DC=corp,DC=local',
};

function emptyGpo() {
  return {
    name: '', target_type: 'domain', target: '', target_ou_id: '', enforced: false,
    link_order: '', status: 'enabled', version: '', description: '',
  };
}

function Badge({ children, tone = 'neutral' }) {
  const tones = {
    neutral: { bg: 'var(--bg-input)', fg: 'var(--text-muted)' },
    amber: { bg: 'rgba(245,158,11,.18)', fg: '#fbbf24' },
    red: { bg: 'rgba(239,68,68,.15)', fg: '#fca5a5' },
    green: { bg: 'rgba(16,185,129,.18)', fg: '#6ee7b7' },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span style={{
      background: t.bg, color: t.fg, fontSize: '0.66rem', fontWeight: 700,
      borderRadius: 4, padding: '1px 6px', textTransform: 'uppercase', letterSpacing: '.03em',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

export default function ADServiceGpoSection({ gpos, onChange, editMode, ous = [], domainName = '' }) {
  const rows = Array.isArray(gpos) ? gpos : [];

  const ouDns = buildOuDns(ous, domainName);
  const ouDepth = (ou) => {
    const byId = {};
    ous.forEach(o => { byId[o.id] = o; });
    let depth = 0;
    const seen = new Set([ou.id]);
    let cur = byId[ou.parent_id];
    while (cur && !seen.has(cur.id)) { seen.add(cur.id); depth += 1; cur = byId[cur.parent_id]; }
    return depth;
  };

  const update = (idx, patch) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx) => {
    const next = rows.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const tone = { bg: 'rgba(139,92,246,.10)', border: '1px solid rgba(139,92,246,.4)' };

  return (
    <div style={{
      background: tone.bg, border: tone.border, borderRadius: 8, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          Group Policy Objects (GPOs documentadas)
          <span style={{
            marginLeft: 6, fontSize: '0.7rem', background: 'var(--bg-input)',
            color: 'var(--text-muted)', borderRadius: 4, padding: '0 6px',
          }}>
            {rows.length}
          </span>
        </div>
        {editMode && (
          <button className="dv-btn" type="button" onClick={() => onChange([...rows, emptyGpo()])}>
            <PlusIcon style={{ width: 14, height: 14 }} /> Añadir GPO
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Sin GPOs documentadas. Úsalas para dejar por escrito qué directivas existen,
          a qué contenedor se vinculan y con qué prioridad.
        </div>
      ) : editMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((gpo, idx) => (
            <div key={idx} style={{
              border: '1px solid var(--slate-200)', borderRadius: 6, padding: 8,
              background: 'var(--bg-card)',
            }}>
              <div className="row g-2">
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Nombre</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="ej. Default Domain Policy"
                    value={gpo.name || ''}
                    onChange={e => update(idx, { name: e.target.value })}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Vinculada a</label>
                  <select
                    className="form-select form-select-sm"
                    value={gpo.target_type || 'domain'}
                    onChange={e => update(idx, { target_type: e.target.value })}
                  >
                    {TARGET_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Destino (contenedor)</label>
                  {(gpo.target_type || 'domain') === 'ou' ? (
                    <>
                      <select
                        className="form-select form-select-sm"
                        style={{ marginBottom: 4 }}
                        value={gpo.target_ou_id || ''}
                        onChange={e => {
                          const id = e.target.value;
                          update(idx, {
                            target_ou_id: id,
                            target: id ? (ouDns[id] || gpo.target || '') : gpo.target || '',
                          });
                        }}
                      >
                        <option value="">— Elegir OU del catálogo —</option>
                        {ous.map(ou => (
                          <option key={ou.id} value={ou.id}>
                            {'\u00A0'.repeat(ouDepth(ou) * 3)}{ou.name}
                          </option>
                        ))}
                      </select>
                      <input
                        className="form-control form-control-sm"
                        placeholder={TARGET_PLACEHOLDER.ou}
                        value={gpo.target || ''}
                        onChange={e => update(idx, { target: e.target.value, target_ou_id: '' })}
                      />
                    </>
                  ) : (
                    <input
                      className="form-control form-control-sm"
                      placeholder={TARGET_PLACEHOLDER[gpo.target_type || 'domain']}
                      value={gpo.target || ''}
                      onChange={e => update(idx, { target: e.target.value })}
                    />
                  )}
                </div>
                <div className="col-md-2">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Orden de vínculo</label>
                  <input
                    className="form-control form-control-sm"
                    type="number"
                    min="1"
                    placeholder="1 = primera"
                    value={gpo.link_order ?? ''}
                    onChange={e => update(idx, {
                      link_order: e.target.value === '' ? '' : Number(e.target.value),
                    })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Estado</label>
                  <select
                    className="form-select form-select-sm"
                    value={gpo.status || 'enabled'}
                    onChange={e => update(idx, { status: e.target.value })}
                  >
                    {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Versión / revisión</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="ej. 12"
                    value={gpo.version || ''}
                    onChange={e => update(idx, { version: e.target.value })}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>Descripción</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Qué configura y por qué"
                    value={gpo.description || ''}
                    onChange={e => update(idx, { description: e.target.value })}
                  />
                </div>
                <div className="col-md-3 d-flex align-items-center" style={{ paddingTop: 18 }}>
                  <label style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={!!gpo.enforced}
                      onChange={e => update(idx, { enforced: e.target.checked })}
                    />
                    Forzada (enforced / No override)
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <button className="dv-btn" type="button" onClick={() => remove(idx)} title="Eliminar GPO">
                  <TrashIcon style={{ width: 14, height: 14 }} /> Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                {['Nombre', 'Vinculada a', 'Destino', 'Orden', 'Estado', 'Versión', 'Descripción'].map(h => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '4px 8px', color: 'var(--text-muted)',
                    borderBottom: '1px solid var(--slate-300)', whiteSpace: 'nowrap',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((gpo, idx) => (
                <tr key={idx}>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--slate-200)', color: 'var(--text-primary)' }}>
                    {gpo.name}
                    {gpo.enforced && <> <Badge tone="amber" title="Enforced">Forzada</Badge></>}
                    {(gpo.status && gpo.status !== 'enabled') && <> <Badge tone="red">Off</Badge></>}
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--slate-200)', color: 'var(--text-muted)' }}>
                    {(TARGET_TYPES.find(t => t[0] === (gpo.target_type || 'domain')) || [, 'Dominio'])[1]}
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--slate-200)', color: 'var(--text-primary)' }}>
                    {gpo.target || '—'}
                    {gpo.target_ou_id && <> <Badge tone="green" title="Vinculada a una OU del catálogo">OU</Badge></>}
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--slate-200)', color: 'var(--text-primary)' }}>
                    {gpo.link_order || '—'}
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--slate-200)', color: 'var(--text-muted)' }}>
                    {(STATUSES.find(s => s[0] === (gpo.status || 'enabled')) || [, '—'])[1]}
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--slate-200)', color: 'var(--text-muted)' }}>
                    {gpo.version || '—'}
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid var(--slate-200)', color: 'var(--text-muted)' }}>
                    {gpo.description || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
